import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Typography } from "@mui/material";
import ArrowBackIosRoundedIcon from "@mui/icons-material/ArrowBackIosRounded";
import {
  clearEmployeeDetails,
  getEmployeeDetails,
  updateEmployee,
  addEmployee,
} from "redux/actions/EmployeesAction";
import { fetchCompanyDetails } from "redux/actions/companyAction";
import CommonMultiForm from "components/commonMultiForm/CommonMultiForm";
import CommonButton from "components/commonButton/CommonButton";
import Loader from "components/loader/Loader";
import { rolesEnum } from "utils/enumObj";
import { getCountryCode } from "redux/actions/authAction";
import { addingEmployeeForm } from "./FrontDeskData";

// Approval Status Options
const APPROVAL_STATUS_OPTIONS = [
  { label: "Pending", value: "Pending" },
  { label: "Approved", value: "Approved" },
  { label: "Rejected", value: "Rejected" },
];

const FrontDeskAddingEmployee = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const isEditMode = location?.state?.type === "edit";

  const userProfile = useSelector(
    (state) => state?.userProfile?.profileDetails?.data,
  );

  const details = useSelector(
    (state) => state?.getEmployeeDetails?.employeeDetails?.data,
  );

  // Default values for ADD mode
  const addModeDefaults = {
    employeeType: "newemployee",
    approvalStatus: "Pending",
  };

  const cleanValue = (val) => {
    if (!val || val === "NA" || val === "null" || val === "undefined") {
      return "";
    }
    return val;
  };

  // Default values for EDIT mode
  const editModeDefaults = {
    companyEmployeeEmail: cleanValue(details?.email),
    name: cleanValue(details?.name),
    countryCode: cleanValue(details?.countryCode),
    phoneNumber: cleanValue(details?.phoneNumber),
    employeeId: cleanValue(details?.employeeId),
    code: cleanValue(details?.code),
    role: details?.role,
    branchName: details?.branchId,
    billingTypeAccess: details?.billingTypeAccess,
    approvalStatus: details?.approvalStatus || "Pending",
    employeeType: "newemployee",
  };

  // Use appropriate defaults based on mode
  const defautValues = isEditMode ? editModeDefaults : addModeDefaults;

  const role = localStorage?.getItem("role");
  const companyID =
    localStorage.getItem("subCompany")?.length > 0 &&
    JSON.parse(localStorage.getItem("subCompany"));

  useEffect(() => {
    dispatch(fetchCompanyDetails({ id: companyID?.value, type: "newBooking" }));
    dispatch(getCountryCode());
  }, []);

  const selectedCompanyDetails = useSelector(
    (state) => state?.userCompanyDetails?.companyDetails,
  );

  const branchOption =
    selectedCompanyDetails &&
    selectedCompanyDetails?.branch?.map((val) => {
      return { value: val?.id, label: val?.address };
    });

  const employeeDetailsLoader = useSelector(
    (state) => state?.getEmployeeDetails?.loading,
  );

  const selectedCompanyLoader = useSelector(
    (state) => state?.userCompanyDetails?.loading,
  );

  const [edit, setEdit] = useState(false);

  const countryCodeListResponse = useSelector((state) => state?.getCountryCode);
  const countryCodeListData =
    countryCodeListResponse?.countryCodeList?.data?.map((data) => {
      return { label: `${data?.code}`, value: `+${data?.dialCode}` };
    });

  useEffect(() => {
    if (location?.state?.id) {
      dispatch(getEmployeeDetails(location?.state?.id));
      setEdit(true); // Starts in locked/view mode
      return () => {
        dispatch(clearEmployeeDetails());
      };
    }
  }, [location?.state?.id]);

  const formSubmit = (data, country) => {
    // 1. Build base payload with mandatory fields
    const employeeData = {
      companyId: companyID?.value,
      name: data?.name,
      countryCode: country,
      phoneNumber: data?.phoneNumber,
      email: data?.companyEmployeeEmail,
      role: data?.role,
      branchId: data?.branchName,
      billingTypeAccess: Number(data?.billingTypeAccess),
      approvalStatus: data?.approvalStatus || "Pending",
    };

    // 2. Handle optional fields differently for ADD vs EDIT
    if (isEditMode) {
      employeeData.employeeId = data?.employeeId
        ? String(data.employeeId).trim()
        : "";
      employeeData.code = data?.code ? String(data.code).trim() : "";
    } else {
      if (data?.employeeId && String(data.employeeId).trim() !== "") {
        employeeData.employeeId = String(data.employeeId).trim();
      }

      if (data?.code && String(data.code).trim() !== "") {
        employeeData.code = String(data.code).trim();
      }
    }

    // 3. Dispatch based on mode
    if (isEditMode) {
      dispatch(updateEmployee(employeeData, details?.id, navigate));
    } else {
      dispatch(addEmployee(employeeData, navigate));
    }
  };

  const handleNavigate = () => {
    if (rolesEnum.frontdeskRoles?.includes(Number(userProfile?.role))) {
      navigate("/front-desk/employeeProfiles");
    } else {
      navigate("/front-desk/all");
    }
  };

  return (
    <Grid container justifyContent={"center"} className="bgLightBlue br20">
      <Grid item xs={11} lg={11} className="ptb30">
        <Grid container justifyContent={"space-between"}>
          <Box className="justifyStart pb20">
            <ArrowBackIosRoundedIcon
              className="font24 colorGrey mr10"
              role="presentation"
              onClick={handleNavigate}
            />

            <Typography
              variant="head3"
              component={"h1"}
              className={`colorBlack fw600`}
            >
              {location?.state?.profile
                ? "Profile Details"
                : isEditMode
                  ? "Employee Details"
                  : "Enter Employee Details"}
            </Typography>
          </Box>
          {isEditMode && role === "2" && !location?.state?.profile && edit && (
            <CommonButton
              name="Edit"
              iconType="edit"
              iconPlace="front"
              btnClass="btnSm btnEdit"
              onClick={() => setEdit(false)}
            />
          )}
          {/* {isEditMode && role === "2" && !location?.state?.profile && (
            <CommonButton
              name={edit ? "Edit" : "Cancel"}
              iconType={edit ? "edit" : "close"}
              iconPlace="front"
              btnClass={`btnSm ${edit ? "btnEdit" : "btnDelete"}`}
              onClick={() => setEdit(!edit)}
            />
          )} */}
        </Grid>

        {/* ADD MODE */}
        {!isEditMode && branchOption && (
          <CommonMultiForm
            key="add-frontdesk-employee"
            formData={addingEmployeeForm(branchOption, APPROVAL_STATUS_OPTIONS)}
            onSubmittBtn={formSubmit}
            button={"Submit"}
            userProfile={userProfile}
            defaultValues={addModeDefaults}
            countryCodeListData={countryCodeListData}
          />
        )}

        {/* EDIT MODE */}
        {isEditMode && (
          <>
            {!employeeDetailsLoader &&
            !selectedCompanyLoader &&
            branchOption &&
            countryCodeListData &&
            countryCodeListData.length > 0 ? (
              <CommonMultiForm
                key={details?.id || "edit-frontdesk-employee"}
                formData={addingEmployeeForm(
                  branchOption,
                  APPROVAL_STATUS_OPTIONS,
                )}
                onSubmittBtn={formSubmit}
                button={!edit && "Submit"}
                userProfile={userProfile}
                defaultValues={editModeDefaults}
                disabled={edit || false}
                countryCodeListData={countryCodeListData}
              />
            ) : (
              <Loader />
            )}
          </>
        )}
      </Grid>
    </Grid>
  );
};

export default FrontDeskAddingEmployee;
